import { adoptToken, isRecord, requestJson } from "./http.js";

/** The account keys sealed under the master password, and when the emergency kit was made. */
export interface AccountKeyState {
  /** Null for an account not unlocked since the account keys exist. */
  readonly keyEncB64: string | null;
  readonly recoveryCreatedAt: number | null;
}

export interface PasswordChangeInput {
  readonly currentAuthKeyB64: string;
  readonly saltB64: string;
  readonly authKeyB64: string;
  readonly keyEncB64: string;
}

export interface RecoveryKitInput {
  readonly currentAuthKeyB64: string;
  readonly keyEncB64: string;
  readonly authKeyB64: string;
}

function unexpected(): Error {
  return new Error("Unerwartete Antwort vom Server.");
}

export function isAccountKeyState(value: unknown): value is { keyEncB64: string | null; recovery: { createdAt: number } | null } {
  if (!isRecord(value)) {
    return false;
  }
  const keyEncB64 = value["keyEncB64"];
  const recovery = value["recovery"];
  return (typeof keyEncB64 === "string" || keyEncB64 === null) &&
    (recovery === null || (isRecord(recovery) && typeof recovery["createdAt"] === "number"));
}

export async function fetchAccountKeys(): Promise<AccountKeyState> {
  const body = await requestJson("/api/v1/account/keys");
  if (!isAccountKeyState(body)) {
    throw unexpected();
  }
  return { keyEncB64: body.keyEncB64, recoveryCreatedAt: body.recovery === null ? null : body.recovery.createdAt };
}

/** First unlock after the upgrade: stores the adopted account keys. */
export async function storeAccountKeys(keyEncB64: string): Promise<void> {
  const body = await requestJson("/api/v1/account/keys", { method: "PUT", body: { keyEncB64 } });
  if (!isRecord(body) || body["ok"] !== true) {
    throw unexpected();
  }
}

/** Creates or replaces the emergency kit. Returns when it was stored. */
export async function saveRecoveryKit(input: RecoveryKitInput): Promise<number> {
  const body = await requestJson("/api/v1/account/recovery", { method: "PUT", body: input });
  if (!isRecord(body) || body["ok"] !== true || typeof body["createdAt"] !== "number") {
    throw unexpected();
  }
  return body["createdAt"];
}

/** Swaps the password and the sealed account keys. The response carries the new session. */
export async function changeMasterPassword(input: PasswordChangeInput): Promise<void> {
  const body = await requestJson("/api/v1/account/password", { method: "POST", body: input });
  if (!isRecord(body) || body["ok"] !== true) {
    throw unexpected();
  }
  adoptToken(body);
}
